import { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

const OUTPUT_SIZE = 400;

function drawCropToCanvas(image, canvas, crop, scale) {
  const ctx = canvas.getContext("2d");
  if (!ctx || !crop.width || !crop.height) return;

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = Math.floor(crop.width * scaleX);
  canvas.height = Math.floor(crop.height * scaleY);

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
  );
  ctx.restore();
}

const AvatarCropModal = ({ open, imageSrc, onClose, onConfirm, loading }) => {
  const imgRef = useRef(null);
  const hiddenCanvasRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [scale, setScale] = useState(1);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!open) {
      setCrop(undefined);
      setCompletedCrop(undefined);
      setScale(1);
      setPreviewUrl("");
    }
  }, [open]);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 90 }, 1, width, height),
      width,
      height,
    );
    setCrop(initial);
  }, []);

  useEffect(() => {
    if (
      !completedCrop?.width ||
      !completedCrop?.height ||
      !imgRef.current ||
      !hiddenCanvasRef.current
    )
      return;
    drawCropToCanvas(imgRef.current, hiddenCanvasRef.current, completedCrop, scale);
    setPreviewUrl(hiddenCanvasRef.current.toDataURL("image/jpeg", 0.7));
  }, [completedCrop, scale]);

  const handleConfirm = useCallback(() => {
    if (!completedCrop?.width || !completedCrop?.height || !imgRef.current) return;

    const tempCanvas = document.createElement("canvas");
    drawCropToCanvas(imgRef.current, tempCanvas, completedCrop, scale);

    const outputCanvas = document.createElement("canvas");
    outputCanvas.width = OUTPUT_SIZE;
    outputCanvas.height = OUTPUT_SIZE;
    const ctx = outputCanvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(tempCanvas, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    outputCanvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
          onConfirm(file);
        }
      },
      "image/jpeg",
      0.92,
    );
  }, [completedCrop, scale, onConfirm]);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Ajustar foto de perfil</DialogTitle>

      <DialogContent>
        {imageSrc && (
          <Stack spacing={2.5} alignItems="center">
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <ReactCrop
                crop={crop}
                onChange={(_, pct) => setCrop(pct)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
                scale={scale}
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Selecionar recorte"
                  style={{ maxHeight: 300, maxWidth: "100%", display: "block" }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </Box>

            <Box sx={{ width: "100%", px: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Zoom
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <ZoomOutIcon fontSize="small" color="action" />
                <Slider
                  min={1}
                  max={3}
                  step={0.05}
                  value={scale}
                  onChange={(_, v) => setScale(v)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <ZoomInIcon fontSize="small" color="action" />
              </Stack>
            </Box>

            {previewUrl && (
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Como ficará
                </Typography>
                <Stack
                  direction="row"
                  spacing={3}
                  justifyContent="center"
                  alignItems="flex-end"
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Avatar src={previewUrl} sx={{ width: 64, height: 64, mx: "auto" }} />
                    <Typography variant="caption" color="text.secondary">
                      Grande
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Avatar src={previewUrl} sx={{ width: 40, height: 40, mx: "auto" }} />
                    <Typography variant="caption" color="text.secondary">
                      Médio
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Avatar src={previewUrl} sx={{ width: 24, height: 24, mx: "auto" }} />
                    <Typography variant="caption" color="text.secondary">
                      Pequeno
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!completedCrop?.width || loading}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {loading ? "Enviando..." : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarCropModal;
